import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ky from "@/shared/api";
import { Resource } from "@/shared/types.ts";
import {
  PackageOptionChargeType,
  PackageOptionEntity,
} from "@/features/package-options/type.ts";

const PACKAGE_OPTIONS_KEY = "package_options";

export type PackageOptionPayload = {
  name: string;
  type: string;
  price: number;
  chargeType: PackageOptionChargeType;
  dimensions: string;
  description: string;
  isActive: boolean;
  sortOrder: number;
};

export function usePackageOptions() {
  return useQuery<Resource<PackageOptionEntity[]>>({
    queryKey: [PACKAGE_OPTIONS_KEY],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<PackageOptionEntity[]>
        >("/api/v1/admin/package/options", { signal: ctx.signal })
        .json(),
    initialData: { data: [] },
  });
}

export function usePackageOptionById({
  optionId,
  enabled = true,
}: {
  optionId: number | string;
  enabled?: boolean;
}) {
  return useQuery<Resource<PackageOptionEntity> | undefined>({
    queryKey: [PACKAGE_OPTIONS_KEY, `package_option_${optionId}`],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<PackageOptionEntity> | undefined
        >(`/api/v1/admin/package/options/${optionId}`, { signal: ctx.signal })
        .json(),
    enabled,
  });
}

export function useCreatePackageOption() {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<PackageOptionEntity>,
    Error,
    PackageOptionPayload
  >({
    mutationFn: (data) =>
      ky.post("/api/v1/admin/package/options", { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGE_OPTIONS_KEY] });
    },
  });
}

export function useEditPackageOption() {
  const queryClient = useQueryClient();

  return useMutation<
    Resource<PackageOptionEntity>,
    Error,
    { id: number } & PackageOptionPayload
  >({
    mutationFn: ({ id, ...data }) =>
      ky.put(`/api/v1/admin/package/options/${id}`, { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGE_OPTIONS_KEY] });
    },
  });
}

export function useDeletePackageOption() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { optionId: number }>({
    mutationFn: ({ optionId }) =>
      ky.delete(`/api/v1/admin/package/options/${optionId}`).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PACKAGE_OPTIONS_KEY] });
    },
  });
}
